import React from "react";

import { cn } from "../../../lib/utils.js";

import { GitCompareIcon } from "lucide-react";

import { Branch } from "./SidebarPanel/Branch.tsx";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../../Components/ui/select.tsx";
import { Button } from "../../../Components/ui/button.tsx";

import { ActionType } from "../types/zustand.ts";

import { FieldIcon } from "./FieldIcon.tsx";
import { truncate } from "../utils/string.ts";
import { useFormEditorContext } from "../../../Contexts/FormEditorContext.jsx";
import { useLogicV2 } from "../hooks/use-logic-v2.ts";
import { Logic } from "../types/logic.ts";

type SidebarPanelProps = React.HTMLAttributes<HTMLDivElement>;

function SidebarPanel({ className, ...props }: SidebarPanelProps) {
    // const logics = useLogicStore((state) => state.logics);
    const { dispatch } = useLogicV2();
    const { fields, logics, selectedField, endScreen } = useFormEditorContext();
    // const fields = useLogicStore((state) => state.fields);
    // const dispatch = useLogicStore((state) => state.dispatch);

    const activeFieldId = selectedField?.id;

    const activeLogic = (logics as Logic[]).find(
        (logic) => logic.fieldId === activeFieldId,
    );

    const branches = activeLogic?.branches;
    const hasBranches = !!branches?.length;

    const getApplicableFields = () => {
        // This returns all the fields that are positioned after the current active field
        const activeFieldIndex = fields.findIndex(
            (field) => field.id === activeFieldId,
        );

        const startIndex = activeFieldIndex + 1;

        const applicableFields = fields
            .slice(startIndex)
            ?.map((field, index) => ({
                ...field,
                index: index + 1 + startIndex,
            }));

        // Add end screen to the applicable fields if it is present
        const hasEndScreen = !!endScreen?.id;

        if (hasEndScreen) {
            // Change the type from thankyou_screen -> end_screen
            // This is done for the sake of getting the correct icon in the object
            endScreen.type = "end_screen";
            applicableFields.push(endScreen);
        }

        return applicableFields;
    };

    const handleAddBranch = () => {
        if (!activeFieldId) return;
        dispatch({
            type: ActionType.ADD_BRANCH,
            payload: {
                fieldId: activeFieldId,
            },
        });
    };

    const handleOnChange = ({
        defaultTargetId,
    }: {
        defaultTargetId: string;
    }) => {
        dispatch({
            type: ActionType.EDIT_LOGIC,
            payload: { defaultTargetId, fieldId: activeFieldId! },
        });
    };

    return (
        <div
            className={cn(
                "max-h-screen pb-[109px] overflow-y-scroll no-scrollbar",
                className,
            )}
            {...props}
        >
            <div className="flex flex-row items-center gap-2">
                <GitCompareIcon className="text-[#191B33] w-4 h-4" />
                <p className="text-sm text-[#191B33] font-medium">Edit Logic</p>
            </div>
            <p className="text-xs text-gray-700 mt-1 text-left">
                The steps users will take to complete the form
            </p>
            {hasBranches && (
                <>
                    <p className="font-medium text-xs text-left mt-3">
                        First, check if:
                    </p>
                    <div className="mt-2">
                        {branches?.map((branch, index) => (
                            <React.Fragment key={branch.id}>
                                <Branch id={branch.id} />
                                {index < branches.length - 1 && (
                                    <BranchDivider className="my-3" />
                                )}
                            </React.Fragment>
                        ))}
                    </div>
                </>
            )}
            <Button
                className="w-full h-9 mt-2 text-xs"
                onClick={handleAddBranch}
            >
                {hasBranches ? "Add Another Branch" : "Add Branch"}
            </Button>
            <div className="mt-2 bg-gray-50 border border-gray-200 rounded-[8px] p-2">
                <div className="flex flex-col items-start bg-white rounded-[8px] p-2">
                    <p className="text-xs font-medium">
                        All other cases {`->`} GO TO
                    </p>
                    <Select
                        value={activeLogic?.defaultTargetId}
                        onValueChange={(value) =>
                            handleOnChange({ defaultTargetId: value })
                        }
                    >
                        <SelectTrigger className="h-9 mt-2">
                            <SelectValue placeholder="Select..." />
                        </SelectTrigger>
                        <SelectContent className="w-[256px]">
                            {getApplicableFields().map((field) => (
                                <SelectItem
                                    key={field.id}
                                    value={field.id}
                                    className="p-[7px]"
                                >
                                    <div className="flex flex-row items-center gap-2">
                                        <FieldIcon
                                            type={field.type}
                                            index={field.index}
                                            className="h-[22px] w-9 p-[5px] text-[8px]"
                                        />
                                        <p className="text-xs text-[#191B33]">
                                            {truncate(field.title, 22)}
                                        </p>
                                    </div>
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>
        </div>
    );
}

export default SidebarPanel;

type BranchDividerProps = React.HTMLAttributes<HTMLDivElement>;

function BranchDivider({ className, ...props }: BranchDividerProps) {
    return (
        <p
            className={cn(
                "text-left text-xs font-medium text-gray-700",
                className,
            )}
            {...props}
        >
            If above criteria aren't met, then check if:
        </p>
    );
}
